import clsx from 'clsx';
import Image from 'next/image';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import type { SingleValue } from 'react-select';
import { toast } from 'react-toastify';
import { UseContactDetailsFromSelector } from '@/app/(main)/client-detail/components/EditSectionModals/components/UseContactDetailsFromSelector';
import { useEligibleAdultPrimaryPassengers } from '@/app/(main)/client-detail/hooks/useEligibleAdultPrimaryPassengers';
import { ClientEmails } from '@/app/(main)/clients/components/form/ClientEmails';
import { ClientPhones } from '@/app/(main)/clients/components/form/ClientPhones';
import { RelatedPassengerAdditionalPhonesAndEmails } from '@/app/(main)/clients/components/form/RelatedPassengerAdditionalPhonesAndEmails';
import { AirportsList } from '@/app/(main)/clients/create/components/PrimaryPassengerTravelPreferences/components/AirportsList';
import { getAge } from '@/app/(main)/clients/create/utils/getAge';
import {
  getInheritedUnderageContact,
  type PrimaryPassengerContactSource,
} from '@/app/(main)/clients/utils/getInheritedUnderageContact';
import { ImageCropModal } from '@/app/(main)/gallery/components/ImageCrop';
import { SelectGalleryImageModal } from '@/app/(main)/gallery/components/SelectGalleryImageModal';
import { useUploadFileToGallery } from '@/app/(main)/gallery/hooks/useUploadFileToGallery';
import { getFileUrl } from '@/app/(main)/gallery/utils/getFileUrl';
import Checkbox from '@/app/components/Checkbox';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { Input } from '@/app/components/form/Input';
import { Selector } from '@/app/components/form/Selector';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import { SingleDatePicker } from '@/app/components/form/SingleDatePicker';
import { GENDER_OPTIONS } from '@/consts/options';
import { useUser } from '@/context/UserProvider';
import { useModalState } from '@/hooks/useModalState';
import { RelationshipOptions } from '@/types/clientsApiTypes';
import type { GenderType, Maybe, Nullable } from '@/types/common';
import type { GalleryItemData } from '@/types/galleryApiTypes';
import { getConvertedToWebpImages } from '@/utils/getConvertedToWebpImages';
import PhotoIcon from '/public/icons/camera.svg';
import RefreshIcon from '/public/icons/refresh.svg';
import UploadIcon from '/public/icons/upload-cloud.svg';

interface EditBasicInformationFormProps {
  isPrimaryPassenger: boolean;
}

export const EditBasicInformationForm = ({ isPrimaryPassenger }: EditBasicInformationFormProps) => {
  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();

  const fieldPrefix = isPrimaryPassenger ? 'primary_passenger.client' : 'related_passengers.0.client';
  const airportsFieldPrefix = isPrimaryPassenger ? 'primary_passenger' : 'related_passengers.0';
  const isCertify = watch(`${fieldPrefix}.userHasCertifiedTheInformation`);
  const gender = watch(`${fieldPrefix}.gender`);
  const profilePictureUrl = watch('primary_passenger.client.profile_picture_url');
  const birthday = watch(`${fieldPrefix}.date_of_birth`) || '';
  const relatedPassengerClientId = watch('related_passengers.0.client.client_id');
  const excludedPassengerId = typeof relatedPassengerClientId === 'number' ? relatedPassengerClientId : null;

  const [showChildTooltip, setShowChildTooltip] = useState(false);
  const contactSourceClientId = watch(`${fieldPrefix}.contact_primary_client_id`) ?? null;
  const [previewUrl, setPreviewUrl] = useState<string | null>(profilePictureUrl ?? null);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const contactDetailsRef = useRef<HTMLDivElement>(null);

  const { isOpen, open, close } = useModalState();
  const { isOpen: isCropModalOpen, open: openCropModal, close: closeCropModal } = useModalState();
  const { uploadFileData, isLoading: isUploadFileDataLoading } = useUploadFileToGallery();

  const { user } = useUser();
  const userId = user?.id ?? null;
  const age = getAge(birthday);
  const isChild = age !== null && age < 18;
  const eligibleAdultPrimaries = useEligibleAdultPrimaryPassengers({
    excludeClientId: excludedPassengerId,
    enabled: !isPrimaryPassenger,
  });
  const showContactSourceSelector = isChild && !isPrimaryPassenger && eligibleAdultPrimaries.length > 0;
  const contactSourceOptions = useMemo(
    () => eligibleAdultPrimaries.map((passenger) => ({ value: passenger.id, label: passenger.label })),
    [eligibleAdultPrimaries]
  );

  const setContactSourceClientId = useCallback(
    (clientId: Nullable<number>) => {
      setValue(`${fieldPrefix}.contact_primary_client_id`, clientId, { shouldValidate: true, shouldDirty: true });
    },
    [fieldPrefix, setValue]
  );

  const applyContactFromSource = useCallback(
    (source: Maybe<PrimaryPassengerContactSource>) => {
      const inherited = getInheritedUnderageContact(source);

      setValue(`${fieldPrefix}.email`, inherited?.email ?? '', { shouldValidate: true });
      setValue(`${fieldPrefix}.phone`, inherited?.phone ?? '', { shouldValidate: true });

      return Boolean(inherited);
    },
    [fieldPrefix, setValue]
  );

  const scrollToContactDetails = useCallback(() => {
    contactDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  useEffect(() => {
    if (!showChildTooltip) return;

    const timer = setTimeout(() => {
      setShowChildTooltip(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [showChildTooltip]);

  const handleCertifyInformationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(`${fieldPrefix}.userHasCertifiedTheInformation`, event.target.checked, { shouldValidate: true });
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    fileInputRef.current?.click();
  };

  const handleDeleteImage = () => {
    setPreviewUrl(null);
    setValue('primary_passenger.client.profile_picture_url', null);
    setValue('primary_passenger.client.profile_picture_gallery_id', null);
    setValue('primary_passenger.client.is_image_deleted', true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (file && file.type.startsWith('image/')) {
        const tempUrl = URL.createObjectURL(file);
        setImageToCrop(tempUrl);
        openCropModal();
      } else {
        setPreviewUrl(null);
        setValue('primary_passenger.client.profile_picture_url', null);
      }
    } catch {
      toast.error('Error uploading image');
      closeCropModal();
    }
  };

  const onChangeDateOfBirth = (date: string) => {
    setValue(`${fieldPrefix}.date_of_birth`, date, { shouldValidate: true });
    const nextAge = getAge(date);

    if (nextAge === null || nextAge >= 18 || isPrimaryPassenger) {
      if (contactSourceClientId !== null) {
        applyContactFromSource(null);
      }
      setContactSourceClientId(null);
      return;
    }

    if (eligibleAdultPrimaries.length === 0) {
      return;
    }

    const [firstEligibleAdult] = eligibleAdultPrimaries;
    const selectedSource =
      eligibleAdultPrimaries.length === 1
        ? firstEligibleAdult
        : eligibleAdultPrimaries.find((passenger) => passenger.id === contactSourceClientId);

    if (selectedSource) {
      setContactSourceClientId(selectedSource.id);
      setShowChildTooltip(applyContactFromSource(selectedSource));
    }

    scrollToContactDetails();
  };

  const handleContactSourceChange = (option: SingleValue<SelectorOption<number>>) => {
    const selectedId = typeof option?.value === 'number' ? option.value : null;
    setContactSourceClientId(selectedId);

    const selectedSource = eligibleAdultPrimaries.find((passenger) => passenger.id === selectedId);
    if (selectedSource) {
      setShowChildTooltip(applyContactFromSource(selectedSource));
    }
  };

  const handleCropSave = async (blob: Blob) => {
    try {
      const file = new File([blob], 'cropped-image.jpg', { type: 'image/jpeg' });
      const convertedImages = await getConvertedToWebpImages([file]);
      const result = await uploadFileData(convertedImages[0]);
      setValue('primary_passenger.client.profile_picture_url', result.image_url);
      setValue('primary_passenger.client.profile_picture_gallery_id', result.gallery_id);
      setValue('primary_passenger.client.is_image_deleted', false);

      setPreviewUrl(result.image_url);
      if (imageToCrop) {
        URL.revokeObjectURL(imageToCrop);
      }
      setImageToCrop(null);
      closeCropModal();
    } catch {
      toast.error('Error uploading image');
      closeCropModal();
    }
  };

  const handleCloseCropModal = () => {
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
    }
    setImageToCrop(null);
    closeCropModal();
  };

  const handleSelectImage = async (image: GalleryItemData) => {
    try {
      close();
      setValue('primary_passenger.client.profile_picture_gallery_id', image.id);
      setPreviewUrl(getFileUrl(image.thumbnail ?? image.path) ?? null);
      setValue('primary_passenger.client.profile_picture_url', getFileUrl(image.thumbnail ?? image.path));
      setValue('primary_passenger.client.is_image_deleted', false);
    } catch {
      toast.error('Error loading image from gallery');
    }
  };

  const handleRelationshipChange = (option: SelectorOption<string | number> | null) => {
    setValue(`${fieldPrefix}.relationship`, option?.value ?? null, { shouldValidate: true });
  };

  return (
    <div className="flex flex-col pb-[30px]" tabIndex={-1} aria-label="Basic Info Section">
      {isPrimaryPassenger && (
        <div className="flex items-center justify-between gap-[24px] w-full border-b border-grey-300 pb-5 mb-5 max-md:flex-col max-md:items-start max-md:gap-4 max-md:pb-4 max-md:mb-4">
          <div className="w-[100px] h-[100px] min-w-[100px] min-h-[100px] bg-neutral-50 border border-grey-400 rounded-full flex items-center justify-center overflow-hidden">
            {previewUrl ? (
              <Image
                width={100}
                height={100}
                src={previewUrl}
                alt="Selected image"
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <PhotoIcon className="w-[36px] h-[36px] text-blue-900 stroke-current" />
            )}
          </div>
          <div className="w-full flex flex-col justify-center gap-[18px]">
            <div className={clsx('flex items-center gap-[10px] max-md:flex-wrap', userId ? 'translate-y-2' : '')}>
              <BaseButton
                startIcon={previewUrl ? <RefreshIcon /> : <UploadIcon />}
                intent="primary"
                size="medium"
                type="button"
                onClick={handleUploadClick}
                disabled={isUploadFileDataLoading}
              >
                {isUploadFileDataLoading
                  ? previewUrl
                    ? 'Replacing...'
                    : 'Uploading...'
                  : previewUrl
                    ? 'Replace Image'
                    : 'Upload File'}
              </BaseButton>
              <input
                type="file"
                accept="jpg, png"
                onChange={handleFileChange}
                ref={fileInputRef}
                style={{ display: 'none' }}
              />
              {previewUrl ? (
                <button
                  onClick={handleDeleteImage}
                  disabled={isUploadFileDataLoading}
                  className="text-red-500 text-body-small font-medium cursor-pointer mt-1 ml-4"
                  type="button"
                >
                  Delete Image
                </button>
              ) : userId ? (
                <BaseButton onClick={open} intent="ghost" size="medium" type="button">
                  Choose from Gallery
                </BaseButton>
              ) : null}
            </div>
            <p className="text-body-small text-grey-600">Accepted formats: JPG, PNG. Other files will be ignored.</p>
          </div>
        </div>
      )}
      <div className="flex flex-col gap-5 max-md:gap-4">
        <div className="lg:col-span-3">
          <Checkbox
            {...register(`${fieldPrefix}.userHasCertifiedTheInformation`)}
            checked={isCertify}
            size="medium"
            variant="secondary"
            labelClassName="!text-black !text-sm"
            onChange={handleCertifyInformationChange}
            label="I certify the information below matches the information that appears on the passenger’s government-issued ID"
          />
        </div>
        <div className="flex gap-5 w-full max-md:flex-col max-md:gap-4">
          <Input
            label="First name"
            placeholder="Enter first name"
            required={isPrimaryPassenger}
            {...register(`${fieldPrefix}.first_name`)}
            errors={errors}
            className="w-1/2 max-md:w-full"
          />
          <Input
            label="Last name"
            placeholder="Enter last name"
            required={isPrimaryPassenger}
            {...register(`${fieldPrefix}.last_name`)}
            errors={errors}
            className="w-1/2 max-md:w-full"
          />
        </div>
        <div className="flex gap-5 w-full max-md:flex-col max-md:gap-4">
          <Input
            label="Middle name"
            placeholder="Enter middle name"
            {...register(`${fieldPrefix}.middle_name`)}
            errors={errors}
            className="w-1/2 max-md:w-full"
          />
          <Input
            label="Preferred name"
            placeholder="Enter the name you prefer to be addressed by"
            {...register(`${fieldPrefix}.preferred_name`)}
            errors={errors}
            className="w-1/2 max-md:w-full"
          />
        </div>
        <div className="flex flex-col gap-5 w-full max-md:gap-4">
          {!isPrimaryPassenger ? (
            <Controller
              name={`${fieldPrefix}.relationship`}
              control={control}
              render={({ field }) => {
                const isLegacy = !!field.value && !RelationshipOptions.some((opt) => opt.value === field.value);
                return (
                  <div>
                    <Selector
                      options={RelationshipOptions}
                      name={`${fieldPrefix}.relationship`}
                      option={isLegacy ? null : field.value}
                      value={isLegacy ? '' : (field.value ?? '')}
                      onChange={handleRelationshipChange}
                      label="Relationship to primary passenger"
                      placeholder={isLegacy ? `${field.value} (legacy)` : 'Select relationship'}
                      menuPosition="fixed"
                      errors={errors}
                    />
                    {isLegacy && (
                      <p className="text-red-500 text-body-small mt-1">
                        Unsupported relationship. Please select a new one.
                      </p>
                    )}
                  </div>
                );
              }}
            />
          ) : null}
          <div className="contents max-md:flex max-md:items-start max-md:gap-4">
            <div className="flex flex-col max-md:flex-1 max-md:min-w-0">
              <Controller
                name={`${fieldPrefix}.date_of_birth`}
                control={control}
                render={({ field }) => (
                  <SingleDatePicker
                    name={`${fieldPrefix}.date_of_birth`}
                    label="Date of birth"
                    intent="primaryWhite"
                    maxDate={new Date()}
                    errors={errors}
                    value={field.value || ''}
                    onChangeDate={onChangeDateOfBirth}
                  />
                )}
              />

              {isChild && !isPrimaryPassenger && (
                <p className="mt-1 text-body-small text-blue-200">
                  This passenger is under 18. Contact details are managed by the Primary Passenger.
                </p>
              )}
            </div>
            <Selector
              className="w-full max-md:flex-1 max-md:min-w-0"
              options={GENDER_OPTIONS}
              value={gender || ''}
              label="Gender"
              onChange={(option) => {
                setValue(`${fieldPrefix}.gender`, (option as SelectorOption<GenderType>).value);
              }}
              placeholder="Select gender"
              menuPosition="fixed"
              maxMenuHeight={220}
              errors={errors}
              name={`${fieldPrefix}.gender`}
            />
          </div>
        </div>
        {isOpen && (
          <SelectGalleryImageModal isOpen={isOpen} onClose={close} onSelect={handleSelectImage} userId={userId} />
        )}
        {isCropModalOpen && imageToCrop ? (
          <ImageCropModal
            isOpen={isCropModalOpen}
            circularCrop
            onClose={handleCloseCropModal}
            imageUrl={imageToCrop}
            onSave={handleCropSave}
            title="Crop  Image"
          />
        ) : null}
      </div>
      <div
        className="mt-5 border-t border-grey-300 pt-5 max-md:mt-4 max-md:pt-4"
        ref={!isPrimaryPassenger ? contactDetailsRef : undefined}
        data-contact-details
      >
        <div className="flex flex-col gap-[2px]">
          <p className="text-body-semibold text-black !text-lg">Contact Details</p>
          <p className="text-body-small text-grey-700">
            {`You can add multiple email addresses and phone numbers. ${isPrimaryPassenger ? 'At least one email is required.' : ''}`}
          </p>
        </div>
        {isPrimaryPassenger ? (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 mt-5 max-md:mt-4 max-md:gap-4">
            <ClientEmails fieldPrefix="primary_passenger.client" isSmallDeleteButton />
            <ClientPhones
              fieldPrefix="primary_passenger.client"
              isSmallDeleteButton
              additionalPhonesLabel="Additional phone number"
            />
          </div>
        ) : (
          <div className="mt-4 flex flex-col gap-5">
            {showContactSourceSelector ? (
              <UseContactDetailsFromSelector
                options={contactSourceOptions}
                value={contactSourceClientId}
                onChange={handleContactSourceChange}
                name={`${fieldPrefix}.contact_primary_client_id`}
                errors={errors}
              />
            ) : null}
            <RelatedPassengerAdditionalPhonesAndEmails
              fieldPrefix={fieldPrefix}
              isChild={isChild}
              showChildTooltip={showChildTooltip}
            />
          </div>
        )}
      </div>
      <div className="mt-5 border-t border-grey-300 pt-5 max-md:mt-4 max-md:pt-4">
        <div className="flex flex-col gap-[2px]">
          <p className="text-body-semibold text-black !text-lg">Preferred home airport(s)</p>
          <p className="text-body-small text-grey-700">
            Client’s primary airports typically used for departures and arrivals.
          </p>
        </div>
        <div className="mt-4">
          <AirportsList label="Home airport" fieldPrefix={airportsFieldPrefix} wrapperClassName="mx-0 mt-0 pl-0" />
        </div>
      </div>
    </div>
  );
};
